/* AdminBeveiligingPage — 3 tabs voor directie:
 * - Geofence-config (Inklok Sprint 1)
 * - BSN-audit dashboard
 * - VOG-waarschuwingen
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Shield, MapPin, Eye, AlertTriangle, Save, Loader2 } from "lucide-react";

export default function AdminBeveiligingPage() {
  return (
    <div className="min-h-screen bg-neutral-50 p-4 md:p-6" data-testid="beveiliging-page">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-2xl md:text-3xl font-display mb-4 flex items-center gap-2">
          <Shield className="h-6 w-6 text-emerald-600" /> Beveiliging & Compliance
        </h1>
        <Tabs defaultValue="geofence">
          <TabsList className="bg-white">
            <TabsTrigger value="geofence" data-testid="tab-geofence">
              <MapPin className="h-4 w-4 mr-1.5" /> Inklok-geofence
            </TabsTrigger>
            <TabsTrigger value="bsn" data-testid="tab-bsn">
              <Eye className="h-4 w-4 mr-1.5" /> BSN-audit
            </TabsTrigger>
            <TabsTrigger value="vog" data-testid="tab-vog">
              <AlertTriangle className="h-4 w-4 mr-1.5" /> VOG-waarschuwingen
            </TabsTrigger>
          </TabsList>
          <TabsContent value="geofence" className="mt-4"><GeofenceTab /></TabsContent>
          <TabsContent value="bsn" className="mt-4"><BsnTab /></TabsContent>
          <TabsContent value="vog" className="mt-4"><VogTab /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function GeofenceTab() {
  const [locations, setLocations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/inklok/config-overview");
      setLocations(r.data.items || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const openEdit = async (loc_id) => {
    try {
      const r = await api.get(`/locations/${loc_id}/geofence`);
      setSelected(loc_id);
      setForm(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const save = async () => {
    if (form.enabled && (form.lat == null || form.lng == null)) {
      toast.error("Coördinaten verplicht als geofence enabled"); return;
    }
    setBusy(true);
    try {
      await api.put(`/locations/${selected}/geofence`, {
        enabled: form.enabled, lat: form.lat, lng: form.lng,
        radius_m: form.radius_m, max_accuracy_m: form.max_accuracy_m,
        timezone: form.timezone,
      });
      toast.success("Geofence opgeslagen");
      setSelected(null); setForm(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="geofence-tab">
      <Card className="p-3 bg-emerald-50 border-emerald-200 text-xs text-emerald-900">
        <strong>Sprint 1 van inkloksysteem</strong> — configureer per locatie waar en hoe strikt medewerkers mogen inklokken. Kloktijd-registratie (sprint 2) en salarisexport (sprint 3) volgen.
      </Card>
      <div className="space-y-2">
        {locations.map(l => (
          <Card key={l.location_id} className="p-3 bg-white flex items-center gap-3" data-testid={`geo-row-${l.location_id}`}>
            <MapPin className={`h-5 w-5 ${l.geofence_enabled ? "text-emerald-600" : "text-neutral-300"}`} />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm">{l.location_name}</div>
              <div className="text-xs text-neutral-500">
                {l.geofence_enabled
                  ? `Actief — radius ${l.radius_m}m, ${l.timezone}`
                  : l.geofence_configured ? "Geconfigureerd (uitgeschakeld)" : "Nog niet ingesteld"}
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={() => openEdit(l.location_id)}
                    data-testid={`geo-edit-${l.location_id}`}>Instellen</Button>
          </Card>
        ))}
      </div>

      {selected && form && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="geo-modal">
            <div className="p-4 border-b font-semibold">Geofence: {form.location_name}</div>
            <div className="p-4 space-y-3">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={form.enabled} onCheckedChange={v => setForm({ ...form, enabled: v })} data-testid="geo-enabled" />
                Geofence-inklokken activeren
              </label>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Latitude</Label>
                  <Input type="number" step="0.0001" value={form.lat ?? ""}
                         onChange={e => setForm({ ...form, lat: e.target.value ? parseFloat(e.target.value) : null })}
                         data-testid="geo-lat" /></div>
                <div><Label className="text-xs">Longitude</Label>
                  <Input type="number" step="0.0001" value={form.lng ?? ""}
                         onChange={e => setForm({ ...form, lng: e.target.value ? parseFloat(e.target.value) : null })}
                         data-testid="geo-lng" /></div>
                <div><Label className="text-xs">Radius (m)</Label>
                  <Input type="number" min="10" max="2000" value={form.radius_m}
                         onChange={e => setForm({ ...form, radius_m: parseInt(e.target.value) || 100 })} /></div>
                <div><Label className="text-xs">Max GPS-afwijking (m)</Label>
                  <Input type="number" min="5" max="500" value={form.max_accuracy_m}
                         onChange={e => setForm({ ...form, max_accuracy_m: parseInt(e.target.value) || 50 })} /></div>
                <div className="col-span-2"><Label className="text-xs">Tijdzone</Label>
                  <Input value={form.timezone}
                         onChange={e => setForm({ ...form, timezone: e.target.value })}
                         placeholder="Europe/Amsterdam" /></div>
              </div>
              <p className="text-xs text-neutral-500">
                Tip: gebruik <a href="https://www.openstreetmap.org" target="_blank" rel="noreferrer" className="underline">openstreetmap.org</a> — rechtermuisklik → "Toon adres" om coördinaten te vinden.
              </p>
            </div>
            <div className="p-4 border-t flex justify-between">
              <Button variant="outline" onClick={() => setSelected(null)}>Annuleren</Button>
              <Button onClick={save} disabled={busy} className="text-white" style={{ background: "var(--sage)" }}
                      data-testid="geo-save">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Opslaan
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BsnTab() {
  const [items, setItems] = useState([]);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/audit/bsn-reveals?days=${days}`);
      setItems(r.data.items || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  return (
    <div data-testid="bsn-tab">
      <div className="flex items-center gap-3 mb-3">
        <Label className="text-sm">Periode:</Label>
        <select value={days} onChange={e => setDays(parseInt(e.target.value))}
                className="border rounded px-2 py-1 text-sm bg-white" data-testid="bsn-days">
          <option value="7">Laatste 7 dagen</option>
          <option value="30">Laatste 30 dagen</option>
          <option value="90">Laatste 90 dagen</option>
        </select>
        <span className="text-xs text-neutral-500">{items.length} entries</span>
      </div>
      {loading && <div className="text-center py-6 text-sm text-neutral-500">Laden…</div>}
      {!loading && items.length === 0 && (
        <Card className="p-6 text-center text-sm text-neutral-500 bg-white">
          Geen BSN-onthullingen in deze periode. 🔒
        </Card>
      )}
      {items.length > 0 && (
        <div className="bg-white rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-xs text-neutral-500 uppercase">
              <tr><th className="p-3 text-left">Wanneer</th><th className="p-3 text-left">Door</th>
                  <th className="p-3 text-left">Rol</th><th className="p-3 text-left">Kind</th></tr>
            </thead>
            <tbody>
              {items.map(i => (
                <tr key={i.audit_id} className="border-t border-neutral-100" data-testid={`bsn-row-${i.audit_id}`}>
                  <td className="p-3 text-xs font-mono text-neutral-600">{i.at?.slice(0, 19).replace("T", " ")}</td>
                  <td className="p-3">{i.user_email}</td>
                  <td className="p-3"><Badge variant="outline" className="text-[10px]">{i.user_role}</Badge></td>
                  <td className="p-3">{i.kind_naam}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function VogTab() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/staff-vog-warnings");
        setItems(r.data.items || []);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    })();
  }, []);

  const badge = (status) => {
    const map = {
      geen_vog: { cls: "bg-red-100 text-red-800", label: "Geen VOG" },
      verlopen: { cls: "bg-red-100 text-red-800", label: "Verlopen" },
      verloopt_binnenkort: { cls: "bg-amber-100 text-amber-800", label: "Binnen 6 mnd" },
    };
    const s = map[status] || { cls: "bg-neutral-100", label: status };
    return <Badge className={`${s.cls} text-[10px]`}>{s.label}</Badge>;
  };

  return (
    <div data-testid="vog-tab">
      <Card className="p-3 mb-3 bg-amber-50 border-amber-200 text-xs text-amber-900">
        Deze medewerkers hebben geen geldige VOG of hun VOG verloopt binnenkort.
        In het rooster wordt bij inplannen een waarschuwing getoond.
      </Card>
      {items.length === 0 ? (
        <Card className="p-6 text-center text-sm text-emerald-700 bg-white">
          Alle actieve medewerkers hebben een geldige VOG. ✅
        </Card>
      ) : (
        <div className="bg-white rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-xs text-neutral-500 uppercase">
              <tr><th className="p-3 text-left">Medewerker</th><th className="p-3 text-left">Status</th>
                  <th className="p-3 text-left">Afgegeven</th><th className="p-3 text-left">Verloopt</th></tr>
            </thead>
            <tbody>
              {items.map(m => (
                <tr key={m.medewerker_id} className="border-t border-neutral-100" data-testid={`vog-row-${m.medewerker_id}`}>
                  <td className="p-3 font-medium">{m.naam}</td>
                  <td className="p-3">{badge(m.status)}</td>
                  <td className="p-3 text-xs">{m.vog_afgegeven?.slice(0, 10) || "—"}</td>
                  <td className="p-3 text-xs">
                    {m.verloopt_op}
                    {m.dagen_tot_verloop !== null && m.dagen_tot_verloop < 180 && (
                      <span className="ml-2 text-amber-600">
                        ({m.dagen_tot_verloop < 0 ? "verlopen" : `nog ${m.dagen_tot_verloop} dg`})
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
